// Stock › PMP / valorización (docs/108 B2): precio medio ponderado y valor del inventario por
// producto, tal como los proyecta el libro de valor. Solo lectura. Lo que no tiene costo aprobado
// queda «valorización pendiente» (D-84): se muestra así, no con un costo inventado.
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { decimal, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useResumenProductos } from './datos';
import { coincide } from '../comun/coincide';

export interface SaldoValorizado {
  productoId: string;
  cantidadFisicaRelevante: string;
  cantidadValorizada: string;
  cantidadPendienteValorizacion: string;
  cantidadSinCobertura: string;
  pmp: string | null;
  valorInventario: string;
  valorProvisional: string;
  valorPendiente: string | null;
  estadoValorizacion: string;
}

const ESTADO: Record<string, string> = {
  VALORIZADO: 'Valorizado',
  PARCIALMENTE_VALORIZADO: 'Parcial',
  PENDIENTE_VALORIZACION: 'Pendiente (sin costo)',
  CONFLICTO_VALORIZACION: 'En conflicto',
};

export function Valorizacion() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const [texto, setTexto] = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'stock', 'valorizacion'],
    queryFn: () => api.get<SaldoValorizado[]>('/valorizacion/saldos', { query: { empresaId } }),
    staleTime: 60_000,
  });
  const productos = useResumenProductos(
    empresaId,
    (q.data ?? []).map((s) => s.productoId),
  );
  const filas = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return (q.data ?? [])
      .filter((s) => Number(s.cantidadFisicaRelevante) !== 0 || Number(s.valorInventario) !== 0)
      .filter((s) => !soloPendientes || Number(s.cantidadPendienteValorizacion) > 0)
      .filter((s) => {
        if (!t) return true;
        const p = productos.mapa.get(s.productoId);
        return !!p && coincide(t, p.sku, p.nombre);
      });
  }, [q.data, productos.mapa, texto, soloPendientes]);
  const total = filas.reduce((n, s) => n + Number(s.valorInventario), 0);
  const pendientes = filas.filter((s) => Number(s.cantidadPendienteValorizacion) > 0).length;
  const columnas: Columna<SaldoValorizado>[] = [
    {
      id: 'sku',
      titulo: 'SKU',
      celda: (s) => <span className="mono">{productos.mapa.get(s.productoId)?.sku ?? '…'}</span>,
      ancho: '8rem',
    },
    {
      id: 'producto',
      titulo: 'Producto',
      celda: (s) => productos.mapa.get(s.productoId)?.nombre ?? '…',
    },
    {
      id: 'cantidad',
      titulo: 'Cantidad',
      celda: (s) => decimal(s.cantidadFisicaRelevante),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'pmp',
      titulo: 'PMP',
      celda: (s) => (s.pmp === null ? <span className="apagado">—</span> : pesos(s.pmp)),
      alinear: 'der',
      ancho: '8rem',
    },
    {
      id: 'valor',
      titulo: 'Valor inventario',
      celda: (s) => <b>{pesos(s.valorInventario)}</b>,
      alinear: 'der',
      ancho: '10rem',
    },
    {
      id: 'pendiente',
      titulo: 'Sin costo',
      celda: (s) =>
        Number(s.cantidadPendienteValorizacion) > 0 ? (
          <span className="tono-aviso">{decimal(s.cantidadPendienteValorizacion)}</span>
        ) : (
          ''
        ),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'estado',
      titulo: 'Estado',
      celda: (s) => ESTADO[s.estadoValorizacion] ?? s.estadoValorizacion,
      ancho: '9rem',
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="valorizacion">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar producto"
            placeholder="SKU o nombre"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <label className="campo-check">
          <input
            type="checkbox"
            checked={soloPendientes}
            onChange={(e) => setSoloPendientes(e.target.checked)}
          />{' '}
          Solo con valorización pendiente
        </label>
        <span className="espaciador" />
        <span>
          {filas.length} productos · valor <b>{pesos(String(Math.round(total)))}</b>
          {pendientes ? (
            <span className="tono-aviso"> · {pendientes} con unidades sin costo aprobado</span>
          ) : null}
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Valorización del inventario"
        columnas={columnas}
        filas={filas}
        clave={(s) => s.productoId}
        cargando={q.isLoading}
        vacio="Sin productos valorizados"
      />
    </div>
  );
}
